import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import EditIcon from '@mui/icons-material/Edit';
import { poppins400, poppins500 } from 'theme/common.style';
import DrawIcon from '@mui/icons-material/Draw';

const CustomSignaturePadDialog = ({
  open,
  onClose,
  onSave,
  signatureStatus,
  isViewSignature,
}) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  const [mode, setMode] = useState('DRAW');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [typedSignature, setTypedSignature] = useState('');

  /* ---------- Canvas Init ---------- */
  useEffect(() => {
    if (!open || mode !== 'DRAW') return;

    const timer = setTimeout(() => {
      if (!canvasRef.current) return;
      if (!canvasRef.current.parentElement) return;

      const canvas = canvasRef.current;
      const parentWidth = canvas.parentElement.offsetWidth;

      canvas.width = parentWidth;
      canvas.height = 160;

      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#000';

      ctxRef.current = ctx;
    }, 0); // 👈 wait for DOM mount

    return () => clearTimeout(timer);
  }, [open, mode]);

  /* ---------- Draw Handlers ---------- */
  const startDrawing = (e) => {
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    setIsDrawing(true);
    // setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    ctxRef.current.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    ctxRef.current.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    if (mode === 'DRAW') {
      ctxRef.current.clearRect(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );
      setHasDrawn(false);
    }
    setTypedSignature('');
  };

  /* ---------- Save Signature ---------- */
  const handleSave = () => {
    let base64;

    if (mode === 'DRAW' && hasDrawn) {
      base64 = canvasRef.current.toDataURL('image/png');
    }

    if (mode === 'TYPE' && typedSignature.trim()) {
      const canvas = document.createElement('canvas');
      canvas.width = 650;
      canvas.height = 130;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#000';
      ctx.font = '48px Poppins';
      ctx.textAlign = 'center';
      ctx.fillText(typedSignature, canvas.width / 2, 100);

      base64 = canvas.toDataURL('image/png');
    }

    if (!base64) return;
    onSave(base64);
    clearSignature();
  };
  const isApplyDisabled =
    (mode === 'DRAW' && !hasDrawn) ||
    (mode === 'TYPE' && !typedSignature.trim());

  const userName = useMemo(() => {
    const user = JSON.parse(localStorage.getItem('userDetail') || '{}');
    const name = `${user?.userName ?? ''} ${user?.userSurName ?? ''}`.trim();
    return name || 'User';
  }, []);
  const sessionSignUrl = sessionStorage?.getItem('signUrl');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
          <Box
            sx={{
              backgroundColor: 'rgb(210, 206, 238)',
              height: 40,
              width: 40,
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <DrawIcon
              sx={{
                color: 'rgb(111, 97, 225)',
                fontSize: 24, // optional
              }}
            />
          </Box>
          <Typography
            sx={{ ...poppins500, color: '#000000', fontSize: '24px' }}
          >
            {isViewSignature
              ? signatureStatus
              : sessionSignUrl
              ? 'Update Signature'
              : 'Create Signature'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Mode Toggle */}
        <Box display="flex" gap={2} mb={2}>
          <Button
            fullWidth
            variant={mode === 'TYPE' ? 'contained' : 'outlined'}
            sx={{
              // ✅ SELECTED STATE
              ...(mode === 'TYPE' && {
                backgroundColor: 'rgb(111, 97, 225)',
                color: '#fff',
                border: 'none',
              }),

              // ✅ UNSELECTED STATE → keep MUI default outlined
              ...(mode !== 'TYPE' && {
                background: 'transparent',
                color: 'rgb(111, 97, 225)',
                borderColor: 'rgb(111, 97, 225)',
              }),

              // ✅ HOVER (only when selected)
              '&:hover': {
                ...(mode === 'TYPE'
                  ? {
                      backgroundColor: 'rgb(96, 82, 205)',
                    }
                  : {}),
              },
            }}
            startIcon={<KeyboardIcon />}
            onClick={() => setMode('TYPE')}
          >
            Type
          </Button>
          <Button
            fullWidth
            variant={mode === 'DRAW' ? 'contained' : 'outlined'}
            startIcon={<EditIcon />}
            onClick={() => setMode('DRAW')}
            sx={{
              // ✅ SELECTED STATE
              ...(mode === 'DRAW' && {
                backgroundColor: 'rgb(111, 97, 225)',
                color: '#fff',
                border: 'none',
              }),

              // ✅ UNSELECTED STATE → keep MUI default outlined
              ...(mode !== 'DRAW' && {
                background: 'transparent',
                color: 'rgb(111, 97, 225)',
                borderColor: 'rgb(111, 97, 225)',
              }),

              // ✅ HOVER (only when selected)
              '&:hover': {
                ...(mode === 'DRAW'
                  ? {
                      backgroundColor: 'rgb(96, 82, 205)', // slightly darker
                    }
                  : {}),
              },
            }}
          >
            Draw
          </Button>
        </Box>

        {/* Signature Area */}
        <Box
          sx={{
            background: '#eef1f5',
            p: 2,
            borderRadius: 1,
            minHeight: 180,
          }}
        >
          {mode === 'DRAW' && (
            <canvas
              ref={canvasRef}
              style={{ width: '100%', cursor: 'crosshair' }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
            />
          )}

          {mode === 'TYPE' && (
            <>
              <TextField
                fullWidth
                placeholder="Type your name"
                value={typedSignature}
                inputProps={{ maxLength: 25 }}
                onChange={(e) => setTypedSignature(e.target.value)}
              />
              <Box
                mt={2}
                sx={{
                  textAlign: 'center',
                  fontSize: 32,
                  fontFamily: 'Poppins',
                  background: '#fff',
                  p: 1,
                }}
              >
                {typedSignature || userName}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={clearSignature}
          variant="outlined"
          sx={{
            ...poppins400,
            fontSize: '14px',
            bgcolor: '#fff',
            borderRadius: '8px',
            borderColor: '#1123471A',
            textTransform: 'none',
            width: 100,
            color: '#666666',
            '&:hover': {
              bgcolor: '#fff',
              borderColor: '#1123471A',
            },
          }}
        >
          Clear
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            ...poppins400,
            fontSize: '14px',
            bgcolor: '#fff',
            borderRadius: '8px',
            borderColor: '#1123471A',
            textTransform: 'none',
            width: 100,
            color: '#666666',
            '&:hover': {
              bgcolor: '#fff',
              borderColor: '#1123471A',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isApplyDisabled}
          sx={{
            ...poppins400,
            fontSize: '14px',
            bgcolor: 'rgb(111, 97, 225)',
            borderRadius: '8px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
            // px: 2,
            '&:hover': {
              bgcolor: 'rgb(96, 82, 205)',
            },
          }}
          onClick={handleSave}
        >
          {isViewSignature ? 'Confirm' : 'Apply Signature'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomSignaturePadDialog;
